import React from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    cartItems,
    packagingItems,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getPackagingTotal,
    getGrandTotal,
    getCartItemsCount
  } = useCart();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-fadeIn"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl animate-slideInRight overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ShoppingBag className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-black">Kosár</h2>
              <p className="text-sm opacity-90">{getCartItemsCount()} tétel</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-semibold">A kosár üres</p>
              <p className="text-sm text-gray-400 mt-2">Adj hozzá termékeket a rendeléshez!</p>
            </div>
          ) : (
            <>
              {cartItems.map((item) => (
                <div 
                  key={item.cartItemId} 
                  className="bg-gray-50 rounded-xl p-4 relative"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 pr-2">
                      <h3 className="font-bold text-gray-900">{item.name}</h3>
                      {item.selectedSize && (
                        <p className="text-xs text-gray-600 mt-1">{item.selectedSize}</p>
                      )}
                      {item.selectedOptions && item.selectedOptions.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          + {item.selectedOptions.join(', ')}
                        </p>
                      )}
                      {item.selectedExtras && item.selectedExtras.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Extrák: {item.selectedExtras.join(', ')}
                        </p>
                      )}
                      {item.customerNote && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                          <span className="font-bold text-gray-900">📝 Megjegyzés:</span>{' '}
                          <span className="text-gray-700">{item.customerNote}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromCart(item.cartItemId)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 bg-white rounded-lg p-1">
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="w-4 h-4 text-gray-600" />
                      </button>
                      <span className="font-bold text-gray-900 w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Plus className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                    <p className="text-lg font-black text-[#FFA726]">
                      {item.price * item.quantity} Ft
                    </p>
                  </div>
                </div>
              ))}

              {/* Packaging Items */}
              {packagingItems.length > 0 && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <h4 className="text-sm font-bold text-gray-700 mb-2">Csomagolás</h4>
                  {packagingItems.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm text-gray-600 py-1">
                      <span>{item.name} ({item.quantity}x)</span>
                      <span className="font-semibold">{item.price * item.quantity} Ft</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary and Checkout */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Ételek összesen:</span>
                <span className="font-semibold">{getCartTotal()} Ft</span>
              </div>
              {getPackagingTotal() > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Csomagolás:</span>
                  <span className="font-semibold text-blue-600">{getPackagingTotal()} Ft</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-black text-gray-900 pt-2 border-t border-gray-300">
                <span>Végösszeg:</span>
                <span className="text-[#FFA726]">{getGrandTotal()} Ft</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="w-full py-4 bg-gradient-to-r from-[#FFA726] to-[#FF9800] text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Rendelés leadása
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
